import {useState, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { useTranslation, withTranslation } from "react-i18next";
import debug from "@plannel/utils/debug-util";
import { green } from "@mui/material/colors";
import {LockRounded} from "@mui/icons-material";
import { Box, CardMedia, Typography } from "@mui/material";
import AppCard from "@plannel/components/ui/AppCard";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import { SwiperSlide } from "swiper/react";
import "@plannel/pages/demand-plan/demand-management/swiper-scss/swiper.scss";
import "@plannel/pages/demand-plan/demand-management/swiper-scss/swiper-navigation.scss";
import "@plannel/pages/demand-plan/demand-management/swiper-scss/swiper-pagination.scss";

import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import RatioBarCell from "@plannel/components/aggrid/RatioBarCell";
import numberUtil from "@plannel/utils/number-util";
import dateUtils from "@plannel/utils/date-util";
import codeUtil from "@plannel/pages/utils/code-util";
import colorUtil from "@plannel/pages/utils/color-util";

import dpScenarioComparisonService from "@plannel/services/dp/dp-scenario-comparison-service";
import planVersionService from "@plannel/services/data/plan-version-service";

import {cloneDeep, divide, isEmpty, isEqual, isNull, isUndefined} from "lodash";
import Snackbar from "@plannel/components/Snackbar";
import ScenarioReleaseModal from "@plannel/components/modal/ScenarioReleaseModal";
import { LockButton, UnlockButton } from "@plannel/components/ActionIconButton";
import comparisonComponent from "@plannel/components/layout/ComparisonContainer";
import { ComparisonReleaseBar, SelectableComparison } from "@plannel/components/layout/ComparisonSelection";
import UnconfirmedScenarioNotice, { isUnconfirmedScenarioVersion } from "@plannel/components/UnconfirmedScenarioNotice";

const {
  module: { DP },
  dpVersion: { SCENARIO, DEFAULT, RELEASED, RELEASING },
  dpMeasure: { MF_AMT, FF_AMT },
  dpApproveStatus: { COMPLETED },
  getCurrencySymbol
} = codeUtil;
const {
  colorSet,
  snackBarMessageColor: { inputError, saveSuccess, dataEmpty, networkError },
} = colorUtil;

const TitleCom = ({ planVersion, index, versions, retrieveDeleteFlg, isReleasing }) => {
  if (!planVersion) {
    return null;
  }
  const version = isUndefined(versions) || isUndefined(versions[planVersion?.name]) ? planVersion : versions[planVersion?.name];
  return (
    <>
      <Box
        sx={{
          display: "flex",
          height: "55px",
          flexShrink: 0,
          alignItems: "flex-end",
          justifyContent: "center",
          borderRadius: "5px",
        }}>
        <LeaderboardIcon
          fontSize={"large"}
          sx={{ mb: 0.2 }}
          color={isUndefined(index) ? "primary" : index === 0 ? "success" : "error"}
        />
        <Typography
          variant="subtitle1"
          sx={{ pl: 1, textAlign: "center", fontWeight: "500" }}>
          {version.name}
        </Typography>
      </Box>
      <Box sx={{ position: "absolute", top: "37px", right: "15px" }}>
        {version.statusCd === RELEASED ? null : version.delFlg ? (
          <UnlockButton
            label="lock"
            disabled={isReleasing}
            onClick={() => retrieveDeleteFlg(version)}
          />
        ) : (
          <LockButton
            label="unlock"
            disabled={isReleasing}
            onClick={() => retrieveDeleteFlg(version)}
          />
        )}
      </Box>
    </>
  );
};

const Comparison = ({ title, data, index, versions, retrieveDeleteFlg, dataMax, isReleasing }) => {
  const { t } = useTranslation();
  const { planVersion, gapToFinance, managerAmt, financeAmt, headers, weekHeaders, profit } = data;
  const measureVals = { gapToFinance, managerAmt, financeAmt };
  const userData = JSON.parse(localStorage.getItem("user"));
  const symbol = getCurrencySymbol(data.currencyCd) ?? userData?.currencySymbol ?? "";

  let version = planVersion;
  if (!isUndefined(versions) && !isUndefined(versions[planVersion?.name])) {
    version = versions[planVersion?.name];
  }
  const gapToFinanceSum = gapToFinance?.reduce((pre, cur) => {
    return Number(pre) + Number(cur);
  }, 0);
  const colorFinance = gapToFinanceSum && gapToFinanceSum !== 0 ? "inherit" : "rgba(0, 0, 0, 0.2)!important";

  const paramHeaders = codeUtil.isWeekBucket(version?.planConfigObj?.BUCKET_UNIT) ? weekHeaders ?? data.stringWeekHeaders : headers ?? data.stringHeaders;

  // 세로 배치를 flex 로 둬서 그리드가 남는 높이를 전부 먹게 한다.
  // 카드마다 있던 릴리즈 버튼이 빠진 자리를 고정 calc 로 두면 카드 아래쪽에 빈 공간이 남는다.
  const mainStyle = {
    // 100vh 계산을 쓰지 않는다 — 실제 가용 높이는 #tab-body 가 정하고 뷰포트보다 작다(App.scss 주석).
    height: "100%",
    // 전역 border-box 가 없는 앱이다. content-box 로 두면 위아래 10px 패딩이 높이에 더해져
    // 카드가 제 칸보다 20px 커지고 맨 아래 상태 배지가 잘린다.
    boxSizing: "border-box",
    p: "10px 0",
    pr: 0.5,
    display: "flex",
    flexDirection: "column",
  };

  const chartSeries = [
    {
      field: MF_AMT.name,
      text: t(MF_AMT.name),
      type: "bar",
      typeOptions: { stackId: MF_AMT.name, fill: colorSet(0), barSize: 20 },
      orientation: "left",
      groupName: t("forecast"),
    },
    {
      field: FF_AMT.name,
      text: t(FF_AMT.name),
      type: "area",
      typeOptions: { fill: colorSet(1), stroke: colorSet(1) },
      orientation: "left",
      groupName: t("forecast"),
    },
  ];

  // 릴리즈 버튼은 화면 상단 공통 바로 빠졌다. 카드에는 상태만 남는다.
  // "릴리즈 중"도 상단 바가 보여 주므로 카드에서는 그리지 않는다.
  const ReleaseStatus = ({ version, statusCd, isReleasing = false }) => {
    const { RELEASED } = codeUtil.versionStatusCode;

    useEffect(() => {
      // 알림 벨이 폴링 주기를 앞당기도록 알린다(NotificationBell 의 export-started 수신).
      if (isReleasing) {
        window.dispatchEvent(new CustomEvent('export-started'));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReleasing]);

    if (isEqual(statusCd, RELEASED)) {
      return (
        <AppCard sx={{ display: "flex", width: "100%", height: "40px", minHeight: "40px", p: 0, mt: 1 }}>
          <CardMedia sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: green[700] }}>
            <Typography variant="subtitle1" pl="10px" color="#fff">
              {`${t("released")}`}
            </Typography>
          </CardMedia>
          <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
            <Typography
              variant="subtitle1"
              component="span"
              ml="auto"
              width="50%"
              textAlign="right"
              pr="10px">
              {`${dateUtils.formatDate(version?.releaseTs, "yyyy-MM-dd")}`}
            </Typography>
          </Box>
        </AppCard>
      )
    } else if (isUnconfirmedScenarioVersion(version)) {
      // 확정 전 시나리오 플래닝 버전은 릴리즈할 수 없다. 확정 화면으로 보낸다.
      return <UnconfirmedScenarioNotice moduleCd="DP" />;
    }
    return null;
  };

  const settingChartDatas = (datas, baseDates) => {
    const chartData = [];
    if (datas && baseDates) {
      const visibleMeasures = [MF_AMT.name, FF_AMT.name];
      if (Array.isArray(baseDates)) {
        for (let i = 0; baseDates.length > i; i++) {
          const measure = {};
          measure.yyyymmdd = dateUtils.formatDate(baseDates[i], "yyyy-MM");
          for (const measureNm of visibleMeasures) {
            measure[measureNm] = datas[measureNm][i];
          }
          chartData.push(measure);
        }
      } else {
        const sortHeaders = Object.keys(baseDates)?.sort();
        for (let i = 0; sortHeaders.length > i; i++) {
          const measure = {};
          const sortHeader = sortHeaders[i];
          measure.yyyymmdd = baseDates[sortHeader];
          for (const measureNm of visibleMeasures) {
            measure[measureNm] = datas[measureNm][i];
          }
          chartData.push(measure);
        }
      }
    }
    return chartData;
  };

  const setCurrency = value => {
    if (value) {
      const number = Number(value);
      if (number > 0) {
        return symbol + numberUtil.formatNumber(number);
      }
      return `- ${symbol}${numberUtil.formatNumber(Math.abs(number))}`;
    }
    return null;
  };

  const settingGridDatas = datas => {
    const rows = ["costAmt", "revenue", "profit"];
    const gridData = [
      {
        category: t("demandQty"),
        rate: null,
        value: datas.demandQty,
      },
    ];

    const rate = {};
    const revenue = Number(datas.revenue); // BigInt → Number
    const cost = Number(datas.cost);
    const profit = Number(datas.profit);

    rate[t("revenue")] = revenue === 0 ? 0 : 100;
    rate[GridUtils.gridValueL10N("costAmt")] = Math.round(divide(cost, revenue) * 100);
    rate[t("profit")] = Math.round(divide(profit, revenue) * 100);

    for (const key of rows) {
      const row = {};
      row.category = GridUtils.gridValueL10N(key);
      if (isEqual("costAmt", key)) {
        row.value = datas.cost;
      } else {
        row.value = datas[key];
      }
      gridData.push(row);
    }

    for (const rowData of gridData) {
      if (!isEqual(rowData.category, "rate") && !isEqual(rowData.category, t("demandQty"))) {
        rowData.rate = rate[rowData.category];
      }
    }
    return gridData;
  };

  const numberFormatter = params => {
    const { value, category } = params.data;
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatNumber = numberUtil.formatNumber(value);
    if (isEqual(category, t("demandQty"))) {
      return formatNumber;
    }

    const number = Number(value);
    if (number > 0) {
      return symbol + numberUtil.formatNumber(number);
    }
    return `- ${symbol}${numberUtil.formatNumber(Math.abs(number))}`;

  };

  const gridDefs = [
    { headerName: "realCategory", field: "category" },
    {
      headerName: " ",
      field: "rate",
      valueGetter: params => params.data.rate,
      cellRenderer: RatioBarCell,
    },
    {
      headerName: "value",
      field: "value",
      type: ["rightAligned"],
      valueFormatter: numberFormatter,
    },
  ];

  const getContextMenuItems = (params) => {
    return params.defaultItems.filter(
      (item) => item !== 'export'
    );
  };

  return (
    <Box sx={mainStyle}>
      <TitleCom
        planVersion={version}
        index={index}
        versions={versions}
        isReleasing={isReleasing}
        retrieveDeleteFlg={retrieveDeleteFlg}
      />
      <Box sx={{ width: "100%", height: "calc(60% - 100px)", flexShrink: 0 }}>
        <ChartComposed
          xAxis={["yyyymmdd"]}
          data={settingChartDatas(measureVals, paramHeaders)}
          options={{ minWidth: "33%", minHeight: 210, yAxisDomain: [0, dataMax], isAmt: true }}
          dataSeries={chartSeries}
          tooltipAnimation={false}
          showConfigurationPopup={false}
        />
      </Box>
      <Box sx={{ height: "90px", flexShrink: 0 }}>
        <Box sx={{ width: "100%", height: "40px" }}>
          <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0, backgroundColor: colorFinance }}>
            <CardMedia sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: "#048BA8" }}>
              <Typography
                variant="subtitle1"
                pl="10px"
                color="#fff">
                {t("gapToFinance")}
              </Typography>
            </CardMedia>
            <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
              <Typography
                variant="subtitle1"
                component="span"
                ml="auto"
                textAlign="right"
                pr="10px">
                {setCurrency(gapToFinanceSum)}
              </Typography>
            </Box>
          </AppCard>
        </Box>
        <Box sx={{ width: "100%", height: "40px", mt: 1 }}>
          <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
            <CardMedia sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: "#048BA8" }}>
              <Typography
                variant="subtitle1"
                pl="10px"
                color="#fff">
                {t("expectedProfit")}
              </Typography>
            </CardMedia>
            <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
              <Typography
                variant="subtitle1"
                component="span"
                ml="auto"
                textAlign="right"
                pr="10px">
                {setCurrency(profit)}
              </Typography>
            </Box>
          </AppCard>
        </Box>
      </Box>
      <Box
        className="ag-theme-balham"
        sx={{ flex: 1, minHeight: 0, mt: 1 }}>
        <AgGridReact
          rowData={settingGridDatas(data)}
          columnDefs={GridUtils.getColumnDefs(gridDefs)}
          {...DefaultGridSetting({ title })}
          defaultColDef={{
            flex: 1,
            minWidth: 30,
            suppressHeaderMenuButton: true,
            resizable: true,
          }}
          getContextMenuItems={getContextMenuItems}
          groupDefaultExpanded={-1}
          groupHideOpenParents
        />
      </Box>
      <ReleaseStatus
        version={version}
        statusCd={version?.statusCd}
        isReleasing={isReleasing}
      />
    </Box>
  );
};

const ScenarioComDashboard = ({ t, title }) => {
  const [loading, setLoading] = useState(false);
  const [scenarioDatas, setScenarioDatas] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [versions, setVersions] = useState({});
  const [dataMax, setDataMax] = useState("auto");
  // use only when released version api called
  const [isReleasing, setIsReleasing] = useState(false);

  const selectedVersion = useRef();
  const currentVersionFlg = useRef(false);
  const [allCompleted, setAllCompleted] = useState(false);
  const [isAccessControl, setAccessControl] = useState(false);
  const buttonDisabled = isAccessControl ? !allCompleted || !currentVersionFlg.current : !currentVersionFlg.current;

  // 카드에서 고른 버전 — 릴리즈는 상단 공통 바에서 이 버전 하나로만 실행한다.
  const [selectedCardId, setSelectedCardId] = useState(null);
  // 카드 제목과 같은 규칙: 자물쇠 토글 등으로 갱신된 버전이 있으면 그쪽을 쓴다.
  const resolveVersion = scenarioData => versions[scenarioData?.planVersion?.name] ?? scenarioData?.planVersion;
  const selectedScenario = scenarioDatas?.find(scenarioData => isEqual(scenarioData?.planVersion?.id, selectedCardId));
  const selectedPlanVersion = selectedScenario ? resolveVersion(selectedScenario) : null;
  const isReferenceVersion = isEqual(selectedPlanVersion?.statusCd, codeUtil.versionStatusCode.REFERENCE);
  const releasable = !!selectedPlanVersion
    && !isEqual(selectedPlanVersion.statusCd, RELEASED)
    && !isEqual(selectedPlanVersion.statusCd, RELEASING)
    && !isUnconfirmedScenarioVersion(selectedPlanVersion);

  // 버튼이 회색인 이유를 툴팁으로 알려 준다. DP 는 결재까지 걸려 있어서 이유가 화면에 안 보이면
  // 왜 못 누르는지 알 길이 없다.
  const releaseBlockReason = (() => {
    if (!selectedPlanVersion) {
      return null;
    }
    if (isUnconfirmedScenarioVersion(selectedPlanVersion)) {
      return t("MSG_ScenarioPlanningNotConfirmed");
    }
    if (!releasable) {
      return t("MSG_NoVersionToRelease");
    }
    if (isAccessControl && !allCompleted) {
      return t("MSG_ApprovalNotCompleted");
    }
    if (buttonDisabled) {
      return t("MSG_NoVersionToRelease");
    }
    return null;
  })();

  const saveSummaryObj = versionDatas => {
    if (!isEmpty(versionDatas)) {
      const paramsList = [];

      for (const versionData of versionDatas) {
        const { cost, demandQty, profit, revenue, managerAmt, financeAmt, gapToFinance, headers, weekHeaders } = versionData;

        const params = {
          managerAmt,
          financeAmt,
          gapToFinance,
          cost,
          demandQty,
          profit,
          revenue,
          headers,
          weekHeaders,
          planVersion: versionData.planVersion,
        };

        if (selectedVersion.current.id !== versionData?.planVersion?.id) {
          paramsList.push(params);
        }
      }

      dpScenarioComparisonService.saveSummaryObj(paramsList).catch(e => {
        debug(e);
      });
    }
  };

  const setSummaryObj = (version) => {
    const releaseVersion = cloneDeep(version);

    if (version?.moduleCd === DP) {
      for (const versionData of scenarioDatas) {
        const { cost, demandQty, profit, revenue, managerAmt, financeAmt, gapToFinance, headers, weekHeaders } = versionData;

        const params = {
          managerAmt,
          financeAmt,
          gapToFinance,
          cost,
          demandQty,
          profit,
          revenue,
          headers,
          weekHeaders,
          planVersion: versionData.planVersion,
        };

        if (version.id === versionData?.planVersion?.id) {
          releaseVersion.dpSummaryDto = params;
        }
      }
    }
    return releaseVersion;
  };

  const onClickReleaseButton = version => {
    selectedVersion.current = setSummaryObj(version);
    saveSummaryObj(scenarioDatas);
    setReleaseOpen(true);
  };

  const sortByProfit = list => {
    list.sort((a, b) => {
      if (a.planVersion?.statusCd !== b.planVersion?.statusCd) {
        return b.planVersion?.statusCd.localeCompare(a.planVersion?.statusCd);
      } else {
        if (a.profit > b.profit) {
          return -1;
        }
        if (a.profit < b.profit) {
          return 1;
        }
        return 0;
      }
    });
    return list;
  };

  const retrieveDeleteFlg = version => {
    const versionId = { planVersionId: version?.id };
    planVersionService.delFlg(versionId)
      .then(response => {
        const ver = response.data;
        const versionList = { ...versions };
        versionList[ver.name] = ver;
        setVersions(versionList);
      })
      .catch(e => {
        if (e.response.data) {
          setSnackInfo({ open: true, severity: inputError, content: t(e.response.data) });
        }
        debug(e);
      });
  };

  const retrieveDpScenario = () => {
    dpScenarioComparisonService.get().then(response => {
        if (response.data) {
          if (response.data.message) {
            const { message } = response.data;
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            const scenarios = response?.data?.scenarios;
            if (scenarios && scenarios?.length === 0) {
              setLoading(false);
              return;
            }

            if (scenarios.map(scenario => scenario.planVersion.statusCd).includes(RELEASING)) {
              setIsReleasing(true);
            }

            setScenarioDatas(sortByProfit(scenarios));
            if (scenarios) {
              const arrays = [];
              for (const scenario of scenarios) {
                if (scenario.measureVals?.[MF_AMT.name]) {
                  arrays.push(...scenario.measureVals[MF_AMT.name]);
                }
                if (scenario.measureVals?.[FF_AMT.name]) {
                  arrays.push(...scenario.measureVals[FF_AMT.name]);
                }
              }

              const maxVal = numberUtil.getMaxValueForChart(arrays);
              setDataMax(maxVal);
            }
            setAccessControl(response?.data?.[codeUtil.dpConfig.APPROVAL_ACCESS_CONTROL] ?? false);
            if (response?.data?.approvePostns) {
              const approvals = response.data.approvePostns;
              const allCompletedFlg = approvals.every(approval => isEqual(approval.apvlStatusCd, COMPLETED));
              setAllCompleted(allCompletedFlg);
            } else {
              setAllCompleted(true);
            }
            if (scenarios?.length > 1) {
              const version = {};
              for (const scenario of scenarios) {
                version[scenario.planVersion.name] = scenario.planVersion;
              }
              setVersions(version);
            }
          }
        } else {
          setSnackInfo({ open: true, severity: dataEmpty, content: t("MSG_NoVersion") });
        }
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: networkError, content: t("MSG_NetworkError") });
        debug(e);
      });
  };

  const retrieveCurrentReleased = () => {
    planVersionService.getAll({ moduleCd: DP, statusCds: [SCENARIO, RELEASING] }).then(response => {
      if (response.data?.results) {
        if (response.data?.results[0].moduleCd !== DEFAULT) {
          currentVersionFlg.current = true;
        } else {
          currentVersionFlg.current = false;
        }
      }
      retrieveDpScenario();
    });
  };

  const setVersion = version => {
    if (!isUndefined(version)) {
      selectedVersion.current = version;
    }
  };

  const resetStatus = () => {
    planVersionService
      .resetStatus()
      .then(() => {
        setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: inputError, content: t("MSG_NoVersionToRelease") });
        debug(e);
      });
  };

  const reload = (isSuccess = false) => {
    retrieveCurrentReleased();
    if (isSuccess) {
      resetStatus();
    }
  };

  useEffect(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    if (loading) {
      retrieveCurrentReleased();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <>
      <Box className={"wrap-comparion-dashboard display-block"}>
        <ComparisonReleaseBar
          selectedName={selectedPlanVersion?.name}
          disabled={!releasable || buttonDisabled}
          disabledReason={releaseBlockReason}
          isReleasing={isReleasing}
          onRelease={() => onClickReleaseButton(selectedPlanVersion)}
          label={isReferenceVersion ? t("refVersion") : undefined}
          startIcon={isReferenceVersion ? <LockRounded /> : undefined}
        />
        <comparisonComponent.ComparisonContainer>
          {scenarioDatas?.map((scenarioData, idx) => (
            <SwiperSlide key={scenarioData.planVersion?.id}>
              <SelectableComparison
                selected={isEqual(scenarioData.planVersion?.id, selectedCardId)}
                onSelect={() => setSelectedCardId(prev => (isEqual(prev, scenarioData.planVersion?.id) ? null : scenarioData.planVersion?.id))}>
                <Comparison
                  dataMax={dataMax}
                  title={title}
                  data={scenarioData}
                  index={idx}
                  versions={versions}
                  retrieveDeleteFlg={retrieveDeleteFlg}
                  isReleasing={isReleasing}
                />
              </SelectableComparison>
            </SwiperSlide>
          ))}
        </comparisonComponent.ComparisonContainer>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <ScenarioReleaseModal
        open={releaseOpen}
        close={setReleaseOpen}
        selectScenario={selectedVersion?.current ?? {}}
        service={planVersionService}
        moduleCd={DP}
        reload={reload}
        setIsReleasing={setIsReleasing}
        isRelesing={isReleasing}
        releaseVersion={setVersion}
      />
    </>
  );
};

export default withTranslation()(ScenarioComDashboard);
